// Libraries
import React, { useEffect, useState } from 'react'
import { withFormik, Field, Form } from 'formik'
import styled from 'styled-components'
import Notification from 'cogo-toast'

// Components
import { mediumGrey } from 'css/colors'
import { FlexWrapper, FieldWrapper, Title } from 'css/utils'
import DatePicker from 'shared/datePicker'
import SelectField from 'shared/selectField'
import Button from 'shared/button'
import Required from 'shared/required'
import LinkButton from 'shared/linkButton'
import ReportDetails from './reportDetails'
import {
  getOptions,
  downloadCSV,
  getFileName,
  hasFilters,
  formatDateToSend
} from 'utils'
import { COUNTRIES } from 'shared/catalogs'
import API from 'api/core'

const Content = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding-bottom: 3rem;
`
const SubTitle = styled.h2`
  font-size: 18px;
  color: ${mediumGrey};
  font-weight: 600;
  padding: 0 0 0.5em 0;
`
const FlexEnd = styled(FlexWrapper)`
  justify-content: flex-end;
  padding: 0.3rem 0;
`

const ContentField = styled(FieldWrapper)`
  &:nth-child(1) {
    margin-right: 1rem;
  }
`

/**
 * @typedef {{
 * invoiceStartDate: string | Date,
 * invoiceEndDate: string | Date,
 * paymentStartDate:string | Date,
 * paymentEndDate: string | Date,
 * country: string,
 * report: TypeReport,
 * typeReport: string
 * }} FormikValues
 * @typedef {{
 * total_charged: string,
 * total_condoned: string,
 * total_income: string,
 * total_returned: string,
 * total_to_charge: string,
 * total_unpayable: string,
 * total_cancelled: string,
 * url: string
 * }} TypeReport
 * @typedef {import('formik').FormikProps<FormikValues>} FormikProps
 * @param {FormikProps} props
 */
const GenerateReport = ({
  handleSubmit,
  setFieldValue,
  isSubmitting,
  values,
  errors,
  touched,
  resetForm
}) => {
  const [incomeTypes, setIcomeTypes] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    API.Accounting.GetIncomeTypes(values.country)
      .then(({ income_types: incomeTypes }) => {
        setIcomeTypes([...incomeTypes, 'TODOS'])
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [values.country])

  /**
   * @param {Date} date
   * @param {string} field
   */
  function handleChange (date, field) {
    setFieldValue(field, date)
  }

  function canDownloadCsv () {
    const name = values.report && getFileName(values.report.url)
    downloadCSV(values.report && values.report.url, name)
  }

  const optionsTypes =
    incomeTypes &&
    incomeTypes.map(type => {
      return {
        id: type,
        label: type
      }
    })

  return (
    <div>
      <LinkButton
        url='/accounting/income'
        icon='arrow-left-thick'
        buttonClass='link bottom'
      >
        Ingresos
      </LinkButton>
      <Content>
        <Title>Generar reporte de ingresos</Title>
        <Form onSubmitCapture={handleSubmit}>
          <div>
            <FlexWrapper bottom='2rem'>
              <Field name='typeReport'>
                {({ field }) => (
                  <SelectField
                    {...field}
                    required
                    id='typeReport'
                    name='typeReport'
                    label='Tipo de reporte'
                    placeholder='Seleciona una opción'
                    options={[
                      { id: 'BY_CHARGES', label: 'Cargos' },
                      { id: 'BY_INCOMES', label: 'Ingresos' }
                    ]}
                  />
                )}
              </Field>
            </FlexWrapper>
            <SubTitle>Facturación</SubTitle>
            <FlexWrapper>
              <ContentField>
                <Field name='invoiceStartDate'>
                  {({ field }) => (
                    <DatePicker
                      {...field}
                      name='invoiceStartDate'
                      width='100%'
                      label='Fecha de inicio'
                      placeholderText='DD/MM/YYYY'
                      selected={values.invoiceStartDate}
                      onChange={date => handleChange(date, 'invoiceStartDate')}
                      dateFormat='dd MMMM yyyy'
                      maxDate={new Date()}
                    />
                  )}
                </Field>
                {errors.invoiceStartDate && touched.invoiceStartDate && (
                  <Required message={errors.invoiceStartDate} />
                )}
              </ContentField>
              <ContentField>
                <Field name='invoiceEndDate'>
                  {({ field }) => (
                    <DatePicker
                      {...field}
                      name='invoiceEndDate'
                      width='100%'
                      label='Fecha final'
                      placeholderText={
                        !values.invoiceStartDate
                          ? 'Elige fecha de inicio'
                          : 'DD/MM/YYYY'
                      }
                      selected={values.invoiceEndDate}
                      onChange={date => handleChange(date, 'invoiceEndDate')}
                      dateFormat='dd MMMM yyyy'
                      maxDate={new Date()}
                      minDate={values.invoiceStartDate}
                      disabled={!values.invoiceStartDate}
                    />
                  )}
                </Field>
                {errors.invoiceEndDate && touched.invoiceEndDate && (
                  <Required message={errors.invoiceEndDate} />
                )}
              </ContentField>
            </FlexWrapper>
          </div>
          <div>
            <SubTitle>Cobro</SubTitle>
            <FlexWrapper>
              <ContentField>
                <Field name='paymentStartDate'>
                  {({ field }) => (
                    <DatePicker
                      {...field}
                      name='paymentStartDate'
                      width='100%'
                      label='Fecha de inicio'
                      placeholderText='DD/MM/YYYY'
                      selected={values.paymentStartDate}
                      onChange={date => handleChange(date, 'paymentStartDate')}
                      dateFormat='dd MMMM yyyy'
                      maxDate={new Date()}
                    />
                  )}
                </Field>
                {errors.paymentStartDate && touched.paymentStartDate && (
                  <Required message={errors.paymentStartDate} />
                )}
              </ContentField>
              <ContentField>
                <Field name='paymentEndDate'>
                  {({ field }) => (
                    <DatePicker
                      {...field}
                      name='paymentEndDate'
                      width='100%'
                      label='Fecha final'
                      placeholderText={
                        !values.paymentStartDate
                          ? 'Elige fecha de inicio'
                          : 'DD/MM/YYYY'
                      }
                      selected={values.paymentEndDate}
                      onChange={date => handleChange(date, 'paymentEndDate')}
                      dateFormat='dd MMMM yyyy'
                      maxDate={new Date()}
                      minDate={values.paymentStartDate}
                      disabled={!values.paymentStartDate}
                    />
                  )}
                </Field>
                {errors.paymentEndDate && touched.paymentEndDate && (
                  <Required message={errors.paymentEndDate} />
                )}
              </ContentField>
            </FlexWrapper>
          </div>
          <div>
            <SubTitle>Más filtros</SubTitle>
            <FlexWrapper>
              <FieldWrapper right='1rem'>
                <Field name='country'>
                  {({ field }) => (
                    <SelectField
                      {...field}
                      id='country'
                      name='country'
                      label='Selecciona país'
                      placeholder='Seleciona una opción'
                      options={[
                        { id: 'ALL', label: 'Todos' },
                        ...getOptions(COUNTRIES)
                      ]}
                    />
                  )}
                </Field>
              </FieldWrapper>
              <FieldWrapper>
                <Field name='incomeType'>
                  {({ field }) => (
                    <SelectField
                      {...field}
                      id='incomeType'
                      name='incomeType'
                      label='Selecciona tipo de ingreso'
                      placeholder='Seleciona una opción'
                      options={optionsTypes}
                      loading={loading}
                      disabled={!values.country}
                    />
                  )}
                </Field>
              </FieldWrapper>
            </FlexWrapper>
          </div>
          <ReportDetails report={values.report} />
          <FlexEnd>
            <Button
              type='submit'
              buttonClass='primary'
              icon='file-document'
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Generar reporte
            </Button>
            <Button
              buttonClass='primary'
              icon='eraser'
              onClick={() => resetForm()}
            >
              Limpiar
            </Button>
            {values.report && (
              <Button
                buttonClass='create'
                icon='cloud-download'
                tooltip='Descargar'
                onClick={() => canDownloadCsv()}
              >
                {getFileName(values.report && values.report.url)}
              </Button>
            )}
          </FlexEnd>
        </Form>
      </Content>
    </div>
  )
}
export default withFormik({
  mapPropsToValues: () => ({
    invoiceStartDate: '',
    invoiceEndDate: '',
    paymentStartDate: '',
    paymentEndDate: '',
    country: 'ALL' || '',
    incomeType: 'TODOS' || '',
    report: null,
    typeReport: ''
  }),
  handleSubmit: async (values, { setSubmitting, setFieldValue }) => {
    const filters = {
      type: values.typeReport,
      country: values.country === 'ALL' ? '' : values.country,
      income_type: values.incomeType === 'TODOS' ? '' : values.incomeType,
      billing_start_date: values.invoiceStartDate
        ? formatDateToSend(values.invoiceStartDate)
        : '',
      billing_finish_date: values.invoiceEndDate
        ? formatDateToSend(values.invoiceEndDate)
        : '',
      payment_start_date: values.paymentStartDate
        ? formatDateToSend(values.paymentStartDate)
        : '',
      payment_finish_date: values.paymentEndDate
        ? formatDateToSend(values.paymentEndDate)
        : ''
    }
    const params = { ...hasFilters(filters) }

    setSubmitting(true)
    try {
      const { data, errors } = await API.Accounting.DownloadIncomeCsv(params)
      if (errors) {
        Notification.error(errors.detail)
      } else {
        Notification.success('Archivo generado correctamente')
        setFieldValue('report', data)
      }
    } catch (error) {
      console.log(error)
      Notification.error('Ha ocurrido un error al generar el archivo')
    }
    setSubmitting(false)
  }
})(GenerateReport)
